import React, {
  useCallback,
  useMemo,
  useState,
  useEffect,
  useContext,
} from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import * as Sentry from '@sentry/browser';

import User from '../../../Model/User';
import useComponentMounted from '../../hooks/useComponentMounted';
import { MediaRecorder } from '../../mediaRecorder';
import FirebaseContext, { withFirebaseContextReady } from '../FirebaseContext';
import ChatContext, {
  chatContextInitialState as initialState,
} from './ChatContext';

const ChatContextProvider = ({
  children,
  chatUserId,
  selectedPodId = '',
  sessionStore,
  mediaRecorder,
  logEvent,
  posthog,
  isSmartContextEnabled,
  isVoiceNotePilotCoach,
}) => {
  const [isReady, setIsReady] = useState(false);
  const [chatUser, setChatUser] = useState(initialState.chatUser);
  const [activeChannelId, setActiveChannelId] = useState(initialState.activeChannelId);
  const [focusMessageId, setFocusMessageId] = useState(initialState.focusMessageId);
  const [currentChatPage, setCurrentChatPage] = useState(initialState.currentChatPage);
  const [readOnlyMode, setReadOnlyMode] = useState(true);
  const [showInternalText, setShowInternalText] = useState(false);
  const [channelError, setChannelError] = useState(initialState.channelError);
  const [activeUserId, setActiveUserId] = useState(initialState.activeUserId);
  const [isChatWebViewOpen, setIsChatWebViewOpen] = useState(initialState.isChatWebViewOpen);
  const [smartContextOpen, setSmartContextOpen] = useState(initialState.smartContextOpen);

  const { authUser } = useContext(FirebaseContext);
  const isComponentMountedRef = useComponentMounted();

  useEffect(() => {
    const init = async () => {
      // Get the main user for the chat (this is the user we send messages from)
      const chatUserDoc = await User.getById(chatUserId);

      // Without the chat's user nothing under the provider can render, so the chat stays on its spinner.
      if (!chatUserDoc) {
        Sentry.captureMessage('Chat user not found', { extra: { chatUserId } });
        return;
      }

      if (isComponentMountedRef.current) {
        setChatUser(chatUserDoc);
        setIsReady(true);

        const { isCoachAssistant, isCoachOrAdmin } = sessionStore;
        const isChatUserAuthenticated = chatUserDoc.id === authUser.id;

        const canEdit = isCoachAssistant || isChatUserAuthenticated;
        setReadOnlyMode(!canEdit);

        setShowInternalText(isCoachOrAdmin);
      }
    };

    if (!isReady) {
      init();
    }
  }, [isReady, chatUserId, isComponentMountedRef, sessionStore, authUser.id]);

  const clearFocusMessage = useCallback(() => setFocusMessageId(''), []);

  const context = useMemo(() => ({
    isReady,
    chatUser,
    authUser,
    activeChannelId,
    focusMessageId,
    setFocusMessageId,
    clearFocusMessage,
    selectedPodId,
    sessionStore,
    posthog,
    setActiveChannelId,
    currentChatPage,
    setCurrentChatPage,
    mediaRecorder,
    readOnlyMode,
    showInternalText,
    channelError,
    setChannelError,
    activeUserId,
    setActiveUserId,
    logEvent,
    isChatWebViewOpen,
    setIsChatWebViewOpen,
    smartContextOpen,
    setSmartContextOpen,
    isSmartContextEnabled,
    isVoiceNotePilotCoach,
  }), [
    isReady,
    chatUser,
    authUser,
    activeChannelId,
    focusMessageId,
    setFocusMessageId,
    clearFocusMessage,
    selectedPodId,
    sessionStore,
    posthog,
    setActiveChannelId,
    currentChatPage,
    setCurrentChatPage,
    mediaRecorder,
    readOnlyMode,
    showInternalText,
    channelError,
    setChannelError,
    activeUserId,
    setActiveUserId,
    logEvent,
    isChatWebViewOpen,
    setIsChatWebViewOpen,
    smartContextOpen,
    setSmartContextOpen,
    isSmartContextEnabled,
    isVoiceNotePilotCoach,
  ]);

  return (
    <ChatContext.Provider value={context}>
      {children}
    </ChatContext.Provider>
  );
};

ChatContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
  chatUserId: PropTypes.string.isRequired,
  sessionStore: PropTypes.object.isRequired,
  posthog: PropTypes.object.isRequired,
  mediaRecorder: PropTypes.instanceOf(MediaRecorder).isRequired,
  logEvent: PropTypes.func.isRequired,
  selectedPodId: PropTypes.string,
  isSmartContextEnabled: PropTypes.bool,
  isVoiceNotePilotCoach: PropTypes.bool,
};

ChatContextProvider.defaultProps = {
  selectedPodId: '',
  isSmartContextEnabled: false,
  isVoiceNotePilotCoach: false,
};

export default compose(
  withFirebaseContextReady,
)(ChatContextProvider);
